import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserPlus, Trash2, Users, Mail } from "lucide-react";
import { toast } from "sonner";
import {
  getMembers,
  inviteMember,
  updateMemberRole,
  removeMember,
  type ProjectMember,
} from "@/lib/collabStore";

const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  editor: "Editor",
  viewer: "Viewer",
};

const ROLE_COLORS: Record<string, string> = {
  owner: "bg-primary/10 text-primary border-primary/30",
  editor: "bg-accent/10 text-accent border-accent/30",
  viewer: "bg-secondary text-secondary-foreground border-border",
};

interface Props {
  projectId: string;
  myRole?: "owner" | "editor" | "viewer";
  currentUserId?: string;
}

export default function ProjectMembersPanel({ projectId, myRole, currentUserId }: Props) {
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"editor" | "viewer">("editor");
  const [inviting, setInviting] = useState(false);

  const isOwner = myRole === "owner";

  const load = async () => {
    setLoading(true);
    try {
      const data = await getMembers(projectId);
      setMembers(data);
    } catch {
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [projectId]);

  const handleInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setInviting(true);
    try {
      const member = await inviteMember(projectId, email, inviteRole);
      setMembers((prev) => [...prev, member]);
      setInviteEmail("");
      toast.success(
        member.status === "active"
          ? `${email} added as ${inviteRole}`
          : `Invite sent — ${email} will get access once they sign up`
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to invite");
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (memberId: string, role: "editor" | "viewer") => {
    try {
      const updated = await updateMemberRole(projectId, memberId, role);
      setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, ...updated } : m)));
    } catch {
      toast.error("Failed to change role");
    }
  };

  const handleRemove = async (memberId: string) => {
    try {
      await removeMember(projectId, memberId);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    } catch {
      toast.error("Failed to remove member");
    }
  };

  return (
    <div className="p-4 space-y-5 max-w-2xl">
      <div>
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">Sharing</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          {members.length} {members.length === 1 ? "person" : "people"} with access
        </p>
      </div>

      {isOwner && (
        <div className="border border-border rounded-lg p-4 space-y-3 bg-secondary/20">
          <p className="text-xs font-semibold text-foreground">Invite by Email</p>
          <div className="flex gap-2">
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="teammate@example.com"
              className="flex-1"
              onKeyDown={(e) => e.key === "Enter" && handleInvite()}
            />
            <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as "editor" | "viewer")}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleInvite} disabled={!inviteEmail.trim() || inviting} className="gap-1.5 shrink-0">
              <UserPlus className="h-3.5 w-3.5" /> Invite
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Editors can change anything. Viewers can read and comment only.
          </p>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : members.length === 0 ? (
        <div className="text-center py-12">
          <Users className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">No one else has access yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {members.map((member) => {
            const isMe = !!currentUserId && member.user_id === currentUserId;
            return (
              <div
                key={member.id}
                className="flex items-center gap-3 px-4 py-3 border border-border rounded-lg bg-card"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-card-foreground truncate">
                      {member.name || member.email}
                    </span>
                    {isMe && <span className="text-[10px] text-muted-foreground">(you)</span>}
                    <Badge variant="outline" className={`text-[10px] px-1.5 ${ROLE_COLORS[member.role]}`}>
                      {ROLE_LABELS[member.role]}
                    </Badge>
                    {member.status === "pending" && (
                      <Badge variant="outline" className="text-[10px] px-1.5 gap-1">
                        <Mail className="h-2.5 w-2.5" /> Pending
                      </Badge>
                    )}
                  </div>
                  {member.name && (
                    <span className="text-[11px] text-muted-foreground font-mono">{member.email}</span>
                  )}
                </div>

                {member.role !== "owner" && isOwner && (
                  <Select
                    value={member.role}
                    onValueChange={(v) => handleRoleChange(member.id, v as "editor" | "viewer")}
                  >
                    <SelectTrigger className="w-24 h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                )}

                {member.role !== "owner" && (isOwner || isMe) && (
                  <button
                    onClick={() => handleRemove(member.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors p-1"
                    title={isMe ? "Leave project" : "Remove access"}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
